import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack, Typography, IconButton, Button, Table, TableHead, TableRow, TableCell, TableBody, TextField, MenuItem, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CloseIcon, Replay as ResetIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const currentYear = new Date().getFullYear();

export default function InitialLeaveEntry(){
  const { t } = useTranslation();
  const [year, setYear] = useState(currentYear);
  const [typeCode, setTypeCode] = useState('ALL');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [edits, setEdits] = useState({}); // { [id]: { leaveTypeId?, leaveTypeCode?, startDateLocalYmd?, endDateLocalYmd?, reason? } }
  const [resetDlg, setResetDlg] = useState({ open:false, preview:null });
  const [formHint, setFormHint] = useState('');

  const load = async () => {
    setLoading(true);
    try{
      const data = await api.get(`/leave-requests/admin/list?year=${year}&page=${page+1}&pageSize=${pageSize}&typeCode=${encodeURIComponent(typeCode)}`);
      const items = Array.isArray(data.items) ? data.items.map(x => ({
        id: x.id ?? x.Id,
        employeeId: x.employeeId ?? x.EmployeeId,
        employeeName: x.employeeName ?? x.EmployeeName,
        employeeEmail: x.employeeEmail ?? x.EmployeeEmail,
        leaveTypeId: x.leaveTypeId ?? x.LeaveTypeId,
        leaveTypeCode: x.leaveTypeCode ?? x.LeaveTypeCode,
        startDateLocalYmd: x.startDateLocalYmd ?? x.StartDateLocalYmd,
        endDateLocalYmd: x.endDateLocalYmd ?? x.EndDateLocalYmd,
        businessDays: x.businessDays ?? x.BusinessDays,
        totalDays: x.totalDays ?? x.TotalDays,
        status: x.status ?? x.Status,
        reason: x.reason ?? x.Reason,
        approverComments: x.approverComments ?? x.ApproverComments,
        approvalDateLocalYmd: x.approvalDateLocalYmd ?? x.ApprovalDateLocalYmd,
        requestDateLocalYmd: x.requestDateLocalYmd ?? x.RequestDateLocalYmd,
        currentCycleId: x.currentCycleId ?? x.CurrentCycleId,
      })) : [];
      setRows(items);
      setTotal(data.total||0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, page, pageSize, typeCode]);
  useEffect(() => { (async()=>{ try { const t = await api.get('/leave-types'); setTypes(Array.isArray(t)?t:[]);} catch{} })(); }, []);
  const refreshEmployees = async (q='') => {
    try {
      // Use Employees search to get EmployeeId + FullName
      const res = await api.get(`/departments/employees/search?query=${encodeURIComponent(q)}&limit=500`);   // STAFF-S1 — no 20-row cap
      setEmployees(Array.isArray(res)?res:[]);
    } catch{}
  };
  useEffect(()=>{ refreshEmployees(''); },[]);

  const isDraftValid = (d) => !!(d.employeeId && d.startDate && d.endDate);
  const hasInvalidDrafts = drafts.some(d => !isDraftValid(d));
  const hasEdits = useMemo(() => Object.keys(edits).length > 0, [edits]);
  const addDraft = () => {
    if (hasInvalidDrafts) { setFormHint('Please complete the current draft (Full Name, Start, End) before adding another.'); return; }
    setFormHint('');
    setDrafts(ds => ([...ds, { key: Math.random().toString(36).slice(2), employeeId:'', leaveTypeId: types.find(x=>String(x.code||x.Code).toUpperCase()==='AL')?.id || '', startDate:'', endDate:'', reason:'' }]));
  };
  const removeDraft = (key) => setDrafts(ds => ds.filter(d => d.key !== key));
  const updateDraft = (key, patch) => setDrafts(ds => ds.map(d => d.key===key ? ({ ...d, ...patch }) : d));

  const businessDaysLocal = (start, end) => {
    // Best-effort visual calc via server helper
    return null; // kept simple in UI; authoritative on server
  };

  const bulkSave = async () => {
    const createItems = drafts
      .filter(isDraftValid)
      .map(d => ({ employeeId: Number(d.employeeId), leaveTypeId: Number(d.leaveTypeId), startDateLocalYmd: d.startDate, endDateLocalYmd: d.endDate, reason: d.reason||'' }));

    const updateIds = Object.keys(edits);
    const updates = updateIds.map(id => {
      const r = rows.find(x => String(x.id) === String(id));
      const e = edits[id] || {};
      return {
        Id: Number(id),
        LeaveTypeId: Number(e.leaveTypeId ?? r.leaveTypeId),
        StartDateLocalYmd: e.startDateLocalYmd ?? r.startDateLocalYmd,
        EndDateLocalYmd: e.endDateLocalYmd ?? r.endDateLocalYmd,
        Reason: (e.reason ?? r.reason) || ''
      };
    });

    if (createItems.length === 0 && updates.length === 0) { setFormHint('Nothing to save. Complete the draft or make edits first.'); return; }

    if (updates.length > 0) {
      await api.put('/leave-requests/admin/bulk-update?skipValidation=true', updates);
    }
    if (createItems.length > 0) {
      await api.post('/leave-requests/admin/bulk-create-approved?skipValidation=true', createItems);
    }

    setDrafts([]);
    setEdits({});
    setPage(0);
    setFormHint('');
    await load();
  };
  const cancelDrafts = () => { setDrafts([]); setEdits({}); setFormHint(''); load(); };

  const onResetPreview = async () => {
    const prev = await api.post(`/leave-requests/admin/reset?year=${year}&dryRun=true`);
    setResetDlg({ open:true, preview: prev });
  };
  const onResetExecute = async () => {
    await api.post(`/leave-requests/admin/reset?year=${year}&dryRun=false`);
    setResetDlg({ open:false, preview:null });
    setPage(0);
    await load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">{t('admin.initialLeaveEntry.title','Initial Leave Entry')}</Typography>
        <Box sx={{ flex:1 }} />
        <TextField select size="small" label={t('admin.leaveBalances.year','Year')} value={year} onChange={e=>{setPage(0); setYear(Number(e.target.value));}} sx={{ minWidth: 140 }}>
          {Array.from({length:21},(_,i)=>currentYear+i).map(y=>(<MenuItem key={y} value={y}>{y}</MenuItem>))}
        </TextField>
        <TextField select size="small" label={t('admin.leaveBalances.typeCode','Type Code')} value={typeCode} onChange={e=>{ setPage(0); setTypeCode(e.target.value); }} sx={{ minWidth: 160 }}>
          <MenuItem value="ALL">{t('common.selectAll','Select All')}</MenuItem>
          {types.map(lt => { const code = String(lt.code||lt.Code||'').toUpperCase(); return <MenuItem key={lt.id||lt.Id||code} value={code}>{code}</MenuItem>; })}
        </TextField>
        <IconButton aria-label={t('admin.leaveBalances.refresh','Refresh')} onClick={load} disabled={loading}><RefreshIcon /></IconButton>
        <Button variant="outlined" color="warning" startIcon={<ResetIcon />} onClick={onResetPreview}>{t('admin.initialLeaveEntry.reset','Reset')}</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={addDraft} disabled={hasInvalidDrafts}>{t('admin.initialLeaveEntry.add','Add')}</Button>
        <Button variant="outlined" color="inherit" startIcon={<CloseIcon />} onClick={cancelDrafts} disabled={(drafts.length===0) && !hasEdits}>{t('admin.initialLeaveEntry.cancelDrafts','Discard All')}</Button>
        <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={bulkSave} disabled={(!hasEdits && drafts.length===0) || hasInvalidDrafts}>{t('admin.initialLeaveEntry.bulkSave','Save All')}</Button>
      </Stack>

      {formHint && (<Typography variant="body2" color="warning.main" sx={{ mb:1 }}>{formHint}</Typography>)}
      <Paper sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.initialLeaveEntry.columns.fullName','Full Name')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.leaveType','Leave Type')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.start','Start')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.end','End')}</TableCell>
              <TableCell align="right">{t('admin.initialLeaveEntry.columns.businessDays','Business Days')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.status','Status')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.requestDate','Request Date')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.approvalDate','Approval Date')}</TableCell>
              <TableCell>{t('admin.initialLeaveEntry.columns.actions','Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map(d => (
              <TableRow key={d.key} sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>
                  <TextField select size="small" value={d.employeeId} onChange={e=>updateDraft(d.key,{ employeeId:e.target.value })} sx={{ minWidth: 240 }}>
                    {employees.map(emp => {
                      const id = emp.id || emp.Id; // Employee Id
                      const name = emp.FullName || emp.fullName || ((emp.FirstName||emp.firstName||'') + ' ' + (emp.LastName||emp.lastName||''));
                      return <MenuItem key={id} value={id}>{name}</MenuItem>;
                    })}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField select size="small" value={d.leaveTypeId} onChange={e=>updateDraft(d.key,{ leaveTypeId:e.target.value })} sx={{ minWidth: 140 }}>
                    {types.map(lt => (<MenuItem key={lt.id||lt.Id} value={lt.id||lt.Id}>{(lt.code||lt.Code)}</MenuItem>))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" value={d.startDate} onChange={e=>updateDraft(d.key,{ startDate:e.target.value })} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" value={d.endDate} onChange={e=>updateDraft(d.key,{ endDate:e.target.value })} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell align="right">{/* visual only or compute via helper */}</TableCell>
                <TableCell>Approved</TableCell>
                <TableCell>{d.startDate ? new Date(d.startDate).toISOString().slice(0,10) : ''}</TableCell>
                <TableCell>{d.startDate ? new Date(new Date(d.startDate).getTime()+2*86400000).toISOString().slice(0,10) : ''}</TableCell>
                <TableCell>
                  <IconButton aria-label={t('common.cancel','Cancel')} onClick={()=>removeDraft(d.key)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}

            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.employeeName||r.employeeEmail}</TableCell>
                <TableCell>
                  <TextField select size="small" value={edits[r.id]?.leaveTypeCode ?? r.leaveTypeCode} onChange={(e)=>{
                    const code = e.target.value;
                    const lt = types.find(x => String(x.code||x.Code).toUpperCase() === code);
                    if (!lt) return;
                    setEdits(prev => ({ ...prev, [r.id]: { ...(prev[r.id]||{}), leaveTypeCode: code, leaveTypeId: lt.id||lt.Id } }));
                  }} sx={{ minWidth: 120 }}>
                    {types.map(lt => { const code = String(lt.code||lt.Code||'').toUpperCase(); return <MenuItem key={lt.id||lt.Id||code} value={code}>{code}</MenuItem>; })}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" value={edits[r.id]?.startDateLocalYmd ?? r.startDateLocalYmd} onChange={(e)=>{
                    const v = e.target.value; setEdits(prev => ({ ...prev, [r.id]: { ...(prev[r.id]||{}), startDateLocalYmd: v } }));
                  }} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell>
                  <TextField type="date" size="small" value={edits[r.id]?.endDateLocalYmd ?? r.endDateLocalYmd} onChange={(e)=>{
                    const v = e.target.value; setEdits(prev => ({ ...prev, [r.id]: { ...(prev[r.id]||{}), endDateLocalYmd: v } }));
                  }} InputLabelProps={{ shrink: true }} />
                </TableCell>
                <TableCell align="right">{r.businessDays}</TableCell>
                <TableCell>{r.status}</TableCell>
                <TableCell>{r.requestDateLocalYmd||r.requestDate}</TableCell>
                <TableCell>{r.approvalDateLocalYmd||r.approvalDate}</TableCell>
                <TableCell>
                  <IconButton aria-label={t('common.delete','Delete')} onClick={async ()=>{ await api.delete(`/leave-requests/${r.id}`); await load(); }}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e,newPage)=>setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e)=>{ setPage(0); setPageSize(parseInt(e.target.value,10)); }}
          rowsPerPageOptions={[10,30,50,100]}
        />
      </Paper>

      <Dialog open={resetDlg.open} onClose={()=>setResetDlg({ open:false, preview:null })}>
        <DialogTitle>{t('admin.initialLeaveEntry.resetTitle','Reset Leave Data')}</DialogTitle>
        <DialogContent>
          {resetDlg.preview ? (
            <>
              <Typography variant="body2">{t('admin.initialLeaveEntry.resetPreview','This action will delete')} {resetDlg.preview.requests} {t('admin.initialLeaveEntry.requests','requests')}, {resetDlg.preview.cycles} {t('admin.initialLeaveEntry.cycles','cycles')} {t('admin.initialLeaveEntry.and','and')} {resetDlg.preview.balancesToReset} {t('admin.initialLeaveEntry.balances','balances')}.</Typography>
              <Typography variant="body2" color="warning.main">{t('admin.initialLeaveEntry.cannotUndo','This action cannot be undone.')}</Typography>
            </>
          ) : (
            <Typography variant="body2">{t('admin.initialLeaveEntry.loading','Loading preview...')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setResetDlg({ open:false, preview:null })}>{t('common.cancel','Cancel')}</Button>
          <Button color="warning" variant="contained" onClick={onResetExecute}>{t('admin.initialLeaveEntry.confirmReset','Confirm Reset')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

